<template>
    <!--
        Hoved og sammenklapning ligger i indpakningen, så alle seks filtertyper
        opfører sig ens. Før var det kun afkrydsningsfilteret der kunne klappes
        sammen, og de øvrige fem stod altid åbne.
    -->
    <div>
        <!--
            Søgning inde i selve filteret.
            Målt i produktion: 2.752 aktive select-filtre har over 20 værdier, og
            955 af dem har over 60. Uden søgning skal man rulle gennem dem alle
            for at finde det ene mærke eller den ene socket man kom efter, og de
            valgte ligger spredt ud imellem resten.
        -->
        <div v-if="filter.values && filter.values.length > VAERDI_SOEG_FRA" class="filter-vaerdisoeg">
            <input
                v-model="vaerdiSoegning"
                type="search"
                class="form-control form-control-sm"
                :placeholder="`Søg blandt ${filter.values.length}`"
                :aria-label="`Søg i ${filter.translation_string_ident}`"
                @click.stop
            />
        </div>

        <div class="filter-options py-2">
            <p v-if="vaerdiSoegning && !synligeVaerdier.length" class="filter-vaerdisoeg__tomt">
                Ingen værdier hedder noget med "{{ vaerdiSoegning }}".
            </p>

            <div v-for="value in synligeVaerdier" :key="value.filter_value_slug" class="filter-check-label-wrap">
                <!-- SEO hidden link for each filter value -->
                <a
                  :href="buildFilterUrl(value.filter_value_slug)"
                  class="visually-hidden"
                  tabindex="-1"
                >
                  {{ props.categoryName }} {{ filter.translation_string_ident }} {{ value.displayed_value }}
                </a>
                <div class="form-check d-flex align-items-center justify-content-between">
                    <div class="d-flex align-items-center">
                        <input type="checkbox" class="form-check-input" 
                               :id="`filter-${value.internal_value}`" 
                               :value="value.filter_value_slug" 
                               :checked="isChecked(value.filter_value_slug)" 
                               @change="toggleFilter($event, value.filter_value_slug)" />
                        <label class="form-check-label ms-2" :for="`filter-${value.internal_value}`">
                            {{ value.displayed_value }}
                        </label>
                    </div>
                    <span class="filter-count-badge badge rounded-pill ms-2 js-filter-count-label"
                          :data-filter-id="filter.id"
                          :data-filter-value="value.filter_value_slug">
                        {{ filterCounts[value.filter_value_slug] || 0 }}
                    </span>
                </div>
            </div>

            <!--
                De valgte ligger altid øverst, også når de falder uden for
                grænsen. Ellers forsvinder ens eget valg ud af syne i det
                øjeblik man folder listen sammen igen.
            -->
            <button
                v-if="kanUdvides"
                type="button"
                class="filter-vis-flere"
                @click.stop="visAlle = true"
            >
                Vis alle {{ filter.values.length }} værdier
            </button>
        </div>
    </div>
</template>

<script setup>
    import { onMounted, ref, computed } from 'vue';
    import { getActiveFilters } from '@/Components/FilterManager.vue';

    const props = defineProps(['filter', 'urlParams', 'categoryName']);
    const emit = defineEmits(['filterUpdated']);

    /*
     * Et filter med 200 afkrydsningsfelter er ikke en facet, det er en liste
     * man skal igennem. Målt i produktion: 2.752 aktive select-filtre har over
     * 20 værdier, og 955 har over 60.
     *
     * Derfor to ting: en søgeboks når der er nok værdier til at det gør ondt,
     * og en grænse med "vis alle" så listen ikke fylder skærmen. De værdier
     * brugeren allerede har valgt vises altid, uanset grænsen.
     */
    const VAERDI_SOEG_FRA = 12;
    const VIS_FRA_START = 10;

    const vaerdiSoegning = ref('');
    const visAlle = ref(false);

    // Current selected values for this filter
    const selectedValues = computed(() => {
        if (!props.urlParams) return [];
        const values = props.urlParams.get(props.filter.id);
        return values ? values.split(',') : [];
    });

    // Ref to store product counts for each filter option
    const filterCounts = ref({});

    function hasActiveFilters() {
        return selectedValues.value.length > 0;
    }

    /** Alle værdier der matcher søgningen i feltet. */
    const matchendeVaerdier = computed(() => {
        const alle = props.filter?.values || [];
        const soeg = vaerdiSoegning.value.trim().toLowerCase();

        if (!soeg) return alle;

        return alle.filter((v) => String(v.displayed_value ?? '').toLowerCase().includes(soeg));
    });

    /**
     * De værdier der faktisk tegnes.
     *
     * Søger man, vises alt der matcher: har man skrevet "b650" vil man se alle
     * B650-varianter, ikke de ti første. Ellers skæres der, men de valgte
     * kommer altid med, så ens eget valg ikke forsvinder ud af syne.
     */
    const synligeVaerdier = computed(() => {
        const matchende = matchendeVaerdier.value;

        if (vaerdiSoegning.value.trim() || visAlle.value) return matchende;
        if (matchende.length <= VIS_FRA_START) return matchende;

        const valgte = matchende.filter((v) => isChecked(v.filter_value_slug));
        const resten = matchende.filter((v) => !isChecked(v.filter_value_slug));

        return [...valgte, ...resten].slice(0, Math.max(VIS_FRA_START, valgte.length));
    });

    const kanUdvides = computed(
        () => !vaerdiSoegning.value.trim() && !visAlle.value
            && matchendeVaerdier.value.length > synligeVaerdier.value.length,
    );

    function isChecked(value) {
        return selectedValues.value.includes(value);
    }

    function toggleFilter(event, value) {
        const isChecked = event.target.checked;
        // Clone current params
        const newParams = new URLSearchParams(props.urlParams.toString());
        // Get current values for this filter
        let values = newParams.get(props.filter.id)?.split(',').filter(v => v) || [];
        if (isChecked) {
            if (!values.includes(value)) {
                values.push(value);
            }
        } else {
            values = values.filter(v => v !== value);
        }
        if (values.length > 0) {
            newParams.set(props.filter.id, values.join(','));
        } else {
            newParams.delete(props.filter.id);
        }
        newParams.delete('page');
        // Build array of { id, value } for all active filters
        const allActiveFilters = [];
        for (const [key, val] of newParams.entries()) {
            if (key !== 'sort' && key !== 'page' && val && val.trim() !== '') {
                allActiveFilters.push({ id: key, value: val });
            }
        }
        emit('filterUpdated', allActiveFilters);
    }

    function buildFilterUrl(slug) {
        // Clone current params
        const newParams = new URLSearchParams(props.urlParams.toString());
        // Get current values for this filter
        let values = newParams.get(props.filter.id)?.split(',').filter(v => v) || [];
        // If this value is not already present, add it
        if (!values.includes(slug)) {
            values.push(slug);
        }
        // Remove duplicates
        values = [...new Set(values)];
        if (values.length > 0) {
            newParams.set(props.filter.id, values.join(','));
        } else {
            newParams.delete(props.filter.id);
        }
        newParams.delete('page');
        // Use SSR-safe path (avoid window if possible)
        let basePath = '';
        if (typeof window !== 'undefined') {
            basePath = window.location.pathname;
        } else if (typeof globalThis !== 'undefined' && globalThis.location) {
            basePath = globalThis.location.pathname;
        } else {
            basePath = '/';
        }
        const qs = newParams.toString();
        return qs ? `${basePath}?${qs}` : basePath;
    }

    // Example: Initialize filterCounts (this should be updated dynamically based on queries)
    onMounted(() => {
        filterCounts.value = props.filter.values.reduce((acc, value) => {
            acc[value.filter_value_slug] = 0; // Default count to 0
            return acc;
        }, {});
    });
</script>

<style scoped>
.filter-vaerdisoeg {
    padding: 6px 0 2px;
}

.filter-vaerdisoeg__tomt {
    margin: 4px 0;
    font-size: var(--pc-text-xs, 0.75rem);
    color: var(--pc-text-muted, #8a8a8a);
}

.filter-vis-flere {
    display: block;
    width: 100%;
    margin-top: 6px;
    padding: 4px 0;
    background: none;
    border: none;
    color: var(--pc-accent);
    font-size: var(--pc-text-xs, 0.75rem);
    text-align: left;
    cursor: pointer;
}

.filter-vis-flere:hover {
    text-decoration: underline;
}

    .filter-headline {
        padding: 0.45rem 0.45rem;
        background-color: var(--pc-bg-tertiary);
        border-radius: 0.375rem;
        color: var(--pc-text-primary);
        font-weight: 600;
        transition: all 0.2s ease;
        margin-bottom: 0.25rem;
        border-left: 3px solid var(--pc-accent);
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
        text-decoration: none;
        line-break: anywhere;
    }

    .filter-headline:hover, .filter-headline:focus {
        background-color: var(--pc-bg-hover);
        color: var(--pc-text-primary);
        text-decoration: none;
    }
    
    .filter-title {
        font-size: 0.95rem;
        text-transform: capitalize;
        letter-spacing: 0.01em;
    }

    /*
     * MAALT 16. oktober 2026: 47px fra én filterraekke til den naeste, mens
     * selve raekken kun er 23px hoej. Over halvdelen var luft — 8px polstring
     * foroven og forneden plus 8px margin — og grafikkortsiden blev 10.854px
     * lang. Et filter er en liste man skimmer, ikke en laeseoplevelse.
     *
     * 33px pr. raekke i stedet for 47 giver en tredjedel mindre at rulle
     * igennem, og der er stadig polstring nok til at hover-fladen foeles som
     * et maal man kan ramme.
     */
    .filter-check-label-wrap {
        margin-bottom: 0.1rem;
        padding: 0.25rem 0.4rem;
        border-radius: 0.375rem;
        transition: background-color 0.2s ease;
    }
    
    .filter-check-label-wrap:hover {
        background-color: var(--pc-bg-hover);
    }

    .filter-options {
        padding: 0.5rem;
        background-color: var(--pc-bg-secondary);
        border-radius: 0 0 0.375rem 0.375rem;
        border: 1px solid var(--pc-border);
        border-top: none;
        margin-bottom: 0.5rem;
    }

    .form-check {
        min-height: unset;
        margin-bottom: 0;
    }

    .form-check-input {
        width: 1.1rem;
        height: 1.1rem;
        margin-top: 0;
        cursor: pointer;
        border: 2px solid var(--pc-border-hover, #555577);
        background-color: var(--pc-bg-tertiary);
    }

    .form-check-input:checked {
        background-color: var(--pc-accent);
        border-color: var(--pc-accent);
    }

    .form-check-label {
        font-size: 0.85rem;
        color: var(--pc-text-primary);
        cursor: pointer;
        user-select: none;
        margin-bottom: 0;
    }

    .filter-count-badge {
        background-color: var(--pc-bg-tertiary);
        color: var(--pc-text-secondary);
        border: 1px solid var(--pc-border);
        font-size: 0.78rem;
        font-weight: 500;
        min-width: 2rem;
        text-align: center;
        padding: 0.18rem 0.7rem;
        box-shadow: none;
        vertical-align: middle;
        line-height: 1.2;
    }

    @media (max-width: 576px) {
        .form-check {
            flex-direction: column;
            align-items: flex-start;
        }

        .filter-count-badge {
            margin-top: 0.5rem;
        }
    }

    /* Mobile-friendly adjustments */
    @media (max-width: 767.98px) {
        .form-check-input {
            width: 1.25rem;
            height: 1.25rem;
            margin-right: 0.5rem;
        }
        
        .form-check-label {
            font-size: 0.95rem;
            padding: 0.25rem 0;
        }
        
        .filter-check-label-wrap {
            padding: 0.5rem 0.75rem;
        }
        
        .filter-headline {
            background-color: var(--pc-bg-tertiary);
        }
    }
</style>
